<template>
  <Grid gtc="3fr 1fr" gap="var(--r-space-md)" class="total-info bg-graph-8 shadow3">
    <div class="left q-py-lg q-pl-lg">
      <div class="text-heading-2 text-white-1">Total</div>

      <Grid gaf="column" justify-content="start" gap="var(--r-space-md)" class="badges">
        <Grid gaf="column" gap="var(--r-space-sm)" align-items="center">
          <div class="box bg-graph-likely"></div>
          <div class="text-caption-1 text-white-1">Budget</div>
        </Grid>

        <Grid gaf="column" gap="var(--r-space-sm)" align-items="center">
          <div class="box bg-white-1"></div>
          <div class="text-caption-1 text-white-1">Cost</div>
        </Grid>
      </Grid>

      <CustomChart v-if="chart" show-values-on-hover class="chart-wrapper q-mt-lg" :chart-data="chart" />
    </div>

    <Grid gap="var(--r-space-lg)" class="right q-py-lg q-pr-xl">
      <template v-if="info">
        <section class="text-right">
          <div class="text-body-4 text-white-5">Scenario Budget</div>
          <Grid gaf="column" gap="var(--r-space-xs)" justify-content="end" align-items="center" justify-items="center">
            <span class="text-heading-3 text-white-1">{{ formatPrice(info.budget) || 'N/A' }}</span>
            <span v-if="info.budget" class="text-caption-3 text-white-1">{{ info.currency }}</span>
          </Grid>
        </section>

        <section class="text-right">
          <div class="text-body-4 text-white-5">Scenario Cost</div>
          <Grid gaf="column" gap="var(--r-space-xs)" justify-content="end" align-items="center" justify-items="center">
            <span class="text-heading-3 text-white-1">{{ formatPrice(info.cost) || 'N/A' }}</span>
            <span v-if="info.cost" class="text-caption-3 text-white-1">{{ info.currency }}</span>
          </Grid>
        </section>

        <section class="text-right">
          <div class="text-body-4 text-white-5">Difference</div>
          <Grid gaf="column" gap="var(--r-space-xs)" justify-content="end" align-items="center" justify-items="center">
            <div :class="differenceClassColor" class="text-heading-3">
              {{ formatPrice(info.difference) || 'N/A' }}
            </div>
            <span v-if="info.difference" class="text-caption-3 text-white-1">{{ info.currency }}</span>
          </Grid>
        </section>

        <section class="text-right">
          <div class="text-body-4 text-white-5">Difference %</div>
          <Grid gaf="column" gap="var(--r-space-xs)" justify-content="end" align-items="center" justify-items="center">
            <span :class="differencePercentageClassColor" class="text-heading-3">
              {{ formatPrice(info.difference_as_percentage_from_budget) || 'N/A' }}
            </span>
            <span v-if="info.difference_as_percentage_from_budget" class="text-caption-3 text-white-1">%</span>
          </Grid>
        </section>
      </template>
    </Grid>
  </Grid>
</template>

<script>
import * as apiTo from '@/modular-api'
import { mapState } from 'vuex'
// Components
import CustomChart from '@/modules/financial/shared/custom-chart/CustomChart'

export default {
  components: {
    CustomChart
  },

  data: () => ({
    apiData: null
  }),

  computed: {
    ...mapState('predictions', ['financialPeriodId']),
    ...mapState('scenarios', ['refreshCardsHandler']),

    chart() {
      if (!this.apiData) return
      const barsData = this.apiData

      const bars = barsData.reduce((acc, curr, index) => {
        const budgetBar = this.getBudgetBar(curr, index === barsData.length - 1)
        const costBar = this.getCostBar(curr, index === barsData.length - 1)

        acc.push(...[budgetBar, costBar])

        return acc
      }, [])

      return {
        bars,
        barGap: '10%',
        group: true,
        lines: {
          count: 3,
          classColor: 'bg-secondary-1'
        },
        yLabels: {
          visible: true,
          classColor: 'text-white-2'
        }
      }
    },

    info() {
      if (!this.apiData) return
      const { length, [length - 1]: last } = this.apiData

      return last
    },

    differenceClassColor() {
      const value = this.info.difference

      if (value === null || !Number(value)) return 'text-white-1'
      if (Number(value) > 0) return 'text-primary-3'
      return 'text-warning-2'
    },

    differencePercentageClassColor() {
      const value = this.info.difference_as_percentage_from_budget

      if (value === null || !Number(value)) return 'text-white-1'
      if (Number(value) > 0) return 'text-primary-3'
      return 'text-warning-2'
    }
  },

  watch: {
    financialPeriodId: {
      handler: 'fetchData',
      immediate: true
    },

    refreshCardsHandler() {
      this.fetchData(this.financialPeriodId)
    }
  },

  methods: {
    fetchData(fpId) {
      if (!fpId) return
      apiTo.GET_SCENARIO_WITH_COSTS_AND_BUDGETS(fpId).then(({ data }) => (this.apiData = data.result))
    },

    getSharedBarProperties(data, isLastItem) {
      return {
        label: data.financial_period_abbreviation,
        labelClassColor: 'text-white-2',
        labelClassText: isLastItem ? 'text-caption-2' : 'text-caption-1',
        width: 8
      }
    },

    getBudgetBar(data, isLastItem = false) {
      return {
        ...this.getSharedBarProperties(data, isLastItem),
        data: [{ label: 'budget', value: data.budget || 0, class: 'bg-graph-likely' }]
      }
    },

    getCostBar(data, isLastItem = false) {
      return {
        ...this.getSharedBarProperties(data, isLastItem),
        data: [{ label: 'cost', value: data.cost || 0, class: 'bg-white-1' }]
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.total-info {
  grid-area: total-info;
  height: 296px;

  .chart-wrapper {
    height: 168px !important;
  }

  ::v-deep .chart {
    .bar {
      border-radius: 5px 5px 0 0;
      overflow: hidden;
    }
  }

  .box {
    width: 8px;
    height: 8px;
    border-radius: 1px;
  }

  ::v-deep .bar-labels {
    grid-template-columns: repeat(6, var(--r-space-lg));
  }
}
</style>
